import React, { useState, useCallback } from 'react';
import { Tab } from 'semantic-ui-react';

import GuaranteePurchases from './GuaranteePurchases';
import CancelledGuaranteePurchases from './CancelledGuaranteePurchases';
import IneligiblePurchases from './IneligiblePurchases';
import GuaranteeProductSelector from '../components/GuaranteeProductSelector';
import GuaranteePanel from '../components/GuaranteePanel';
import useFeatureFlag from '../hooks/useFeatureFlag';
import { DB_GUARANTEES_V2 } from '../constants/featureFlags';
import {
  useGuaranteesApiV2,
  useGuaranteeTheme,
  useResetGuaranteeProductOnExit,
} from '../hooks/useGuaranteePurchases';

// The only tab showing the product selector while guarantees_api_v2 is off.
const PRODUCT_SELECTOR_TAB_KEY = 'ineligible';

const GuaranteePurchasesTabs = () => {
  const [isGuaranteesV2Enabled, loaded] = useFeatureFlag(DB_GUARANTEES_V2);
  const [isApiV2Enabled] = useGuaranteesApiV2();
  const themeClass = useGuaranteeTheme();
  // Registered above the flag gate so the cleanup exists even while flags load.
  useResetGuaranteeProductOnExit();
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const handleTabChange = useCallback((e, { activeIndex }) => {
    setActiveTabIndex(activeIndex);
  }, []);

  // Wait for flags so we don't flash the V1 layout before switching to V2.
  if (!loaded) return null;

  if (!isGuaranteesV2Enabled) {
    const panes = [
      {
        menuItem: { key: 'guarantee', content: 'Guarantee Purchases', 'data-testid': 'tab-guarantee' },
        render: () => (
          <Tab.Pane>
            {' '}
            <GuaranteePurchases />
          </Tab.Pane>
        ),
      },
      {
        menuItem: { key: 'cancelled', content: 'Canceled Purchases', 'data-testid': 'tab-cancelled' },
        render: () => (
          <Tab.Pane>
            <CancelledGuaranteePurchases />
          </Tab.Pane>
        ),
      },
    ];

    return (
      <div data-testid='guarantees-tabs'>
        {' '}
        <Tab panes={panes} />
      </div>
    );
  }

  const panes = [
    {
      menuItem: { key: 'guarantee', content: 'Guarantee Purchases', 'data-testid': 'tab-guarantee' },
      render: () => (
        <Tab.Pane>
          <GuaranteePurchases />
        </Tab.Pane>
      ),
    },
    {
      menuItem: { key: 'cancelled', content: 'Cancelled Purchases', 'data-testid': 'tab-cancelled' },
      render: () => (
        <Tab.Pane>
          <CancelledGuaranteePurchases />
        </Tab.Pane>
      ),
    },
    {
      menuItem: { key: 'ineligible', content: 'Ineligible Purchases', 'data-testid': 'tab-ineligible' },
      render: () => (
        <Tab.Pane>
          <IneligiblePurchases />
        </Tab.Pane>
      ),
    },
  ];

  const isProductSelectorTab = panes[activeTabIndex]?.menuItem.key === PRODUCT_SELECTOR_TAB_KEY;

  return (
    <div
      data-testid='guarantees-tabs'
      style={{ marginTop: '-56px' }}
      className={themeClass}
    >
      <GuaranteePanel isHidden={!(isApiV2Enabled || isProductSelectorTab)}>
        <GuaranteeProductSelector />
      </GuaranteePanel>
      <Tab
        panes={panes}
        activeIndex={activeTabIndex}
        onTabChange={handleTabChange}
      />
    </div>
  );
};

export default GuaranteePurchasesTabs;
